import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertCircle, ChevronRight, Sparkles, Bell, Wallet, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { matters as mattersApi, trust as trustApi, auth, dashboard as dashboardApi, firmHealth as firmHealthApi } from '@/lib/api';
import { POLICY_ALERTS } from '@/mock/mockData';
import OnboardingChecklist from '@/components/OnboardingChecklist';

const STAGE_COLORS = {
  INTAKE: 'bg-slate-100 text-slate-700',
  ENGAGED: 'bg-blue-100 text-blue-700',
  IN_PREP: 'bg-amber-100 text-amber-700',
  LODGED: 'bg-indigo-100 text-indigo-700',
  DECIDED: 'bg-emerald-100 text-emerald-700',
};
const RAG_COLORS = { GREEN: 'bg-emerald-500', AMBER: 'bg-amber-500', RED: 'bg-rose-500' };

export default function DashboardPage() {
  const [matters, setMatters] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const user = auth.currentUser();

  React.useEffect(() => {
    Promise.all([mattersApi.list(), trustApi.summary()])
      .then(([m, s]) => { setMatters(m); setSummary(s); })
      .finally(() => setLoading(false));
  }, []);

  const kpis = React.useMemo(() => {
    const openCount = matters.filter((m) => m.stage !== 'DECIDED' && m.stage !== 'CLOSED').length;
    const waiting = matters.filter((m) => m.rag === 'AMBER' || m.rag === 'RED').length;
    return [
      { label: 'Open matters', value: openCount, delta: `${matters.length} total`, tone: 'positive' },
      { label: 'Needs attention', value: waiting, delta: `${matters.filter(m=>m.rag==='RED').length} red flag`, tone: waiting > 0 ? 'warn' : 'positive' },
      { label: 'Trust balance', value: summary ? `A$${summary.trust_total.toLocaleString('en-AU')}` : '—', delta: 'Reconciled 30 Jun', tone: 'positive' },
      { label: 'Client ledgers', value: summary?.open_client_ledgers ?? '—', delta: 'Zero-negative enforced', tone: 'positive' },
    ];
  }, [matters, summary]);

  if (loading) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Good morning, {user?.name?.split(' ')[0] || 'there'}</div>
          <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Today&rsquo;s ImmiPro.</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/app/matters"><Button variant="outline" className="h-10 border-[#1A3A5C]/20">View all matters</Button></Link>
          <Link to="/app/matters"><Button className="h-10 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">+ New matter</Button></Link>
        </div>
      </div>

      <OnboardingChecklist />

      <div className="grid gap-4 md:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500">{k.label}</div>
              <div className="mt-2 font-serif text-3xl font-bold text-[#1A3A5C]">{k.value}</div>
              <div className={`mt-1 flex items-center gap-1 text-xs ${k.tone === 'positive' ? 'text-emerald-600' : k.tone === 'warn' ? 'text-amber-600' : 'text-slate-500'}`}>
                {k.tone === 'positive' ? <TrendingUp className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {k.delta}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <FirmHealthCard />


      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-slate-100 p-5">
              <div>
                <div className="text-sm font-semibold text-[#1A3A5C]">Active matters</div>
                <div className="text-xs text-slate-500">Live from database · sorted by most recent</div>
              </div>
              <Link to="/app/matters" className="text-xs font-medium text-[#2ECC9A] hover:underline">See all →</Link>
            </div>
            <div className="divide-y divide-slate-100">
              {matters.slice(0, 5).map((m) => (
                <Link key={m.id} to={`/app/matters/${m.id}`} className="flex items-center justify-between px-5 py-4 transition-colors hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`h-2.5 w-2.5 rounded-full ${RAG_COLORS[m.rag] || 'bg-slate-300'}`} />
                    <div>
                      <div className="text-sm font-medium text-[#1A3A5C]">{m.applicant}</div>
                      <div className="text-xs text-slate-500">{m.ref} · Subclass {m.subclass}{m.stream ? ` · ${m.stream}` : ''}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={(STAGE_COLORS[m.stage] || 'bg-slate-100 text-slate-700') + ' text-[10px] uppercase tracking-wider'}>{(m.stage||'').replace('_',' ')}</Badge>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">{m.deadline_label}</div>
                      <div className="text-xs font-medium text-[#1A3A5C]">{m.next_deadline || '—'}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-slate-200 bg-gradient-to-br from-[#1A3A5C] to-[#22507A] text-white">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]">
                <Sparkles className="h-3.5 w-3.5" /> AI Copilot
              </div>
              <div className="mt-3 text-sm text-white/90">Real Emergent LLM wired up. Try the Qualification→Occupation suggester and the RFI drafter — both call the model live.</div>
              <Link to="/app/copilot" className="mt-4 inline-flex items-center text-xs font-medium text-[#2ECC9A] hover:underline">Open Copilot →</Link>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]">
                <Wallet className="h-3.5 w-3.5" /> Trust account
              </div>
              <div className="mt-3 font-serif text-3xl font-bold text-[#1A3A5C]">A${summary?.trust_total.toLocaleString('en-AU') || '—'}</div>
              <div className="mt-1 text-xs text-emerald-600">Variance A${summary?.variance.toFixed(2) || '0.00'} · reconciled</div>
              <Link to="/app/trust" className="mt-4 inline-flex items-center text-xs font-medium text-[#2ECC9A] hover:underline">Open trust ledger →</Link>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]">
                <Bell className="h-3.5 w-3.5" /> Policy pulse
              </div>
              <div className="mt-3 space-y-2 text-sm">
                {POLICY_ALERTS.slice(0, 2).map((a) => (
                  <div key={a.id} className="rounded-md border border-slate-100 bg-slate-50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{a.source}</span>
                      <span className="text-[10px] text-slate-500">{a.published}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-[#1A3A5C]">{a.title}</div>
                    <div className="mt-1 text-xs text-amber-600">Impacts {a.impacted} matter{a.impacted !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
              <Link to="/app/policy" className="mt-4 inline-flex items-center text-xs font-medium text-[#2ECC9A] hover:underline">See all alerts →</Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const BAND_STYLES = {
  green: { bar: 'bg-emerald-500', text: 'text-emerald-700', ring: 'ring-emerald-200', tint: 'from-emerald-50', dot: 'bg-emerald-500' },
  amber: { bar: 'bg-amber-500', text: 'text-amber-700', ring: 'ring-amber-200', tint: 'from-amber-50', dot: 'bg-amber-500' },
  red: { bar: 'bg-red-500', text: 'text-red-700', ring: 'ring-red-200', tint: 'from-red-50', dot: 'bg-red-500' },
};

function FirmHealthCard() {
  const [h, setH] = React.useState(null);
  const [history, setHistory] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    Promise.all([dashboardApi.health(), firmHealthApi.history(30).catch(() => null)])
      .then(([health, hist]) => { setH(health); setHistory(hist); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !h) {
    return (
      <Card className="border-slate-200"><CardContent className="flex items-center justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></CardContent></Card>
    );
  }

  const overallStyle = BAND_STYLES[h.overall.band] || BAND_STYLES.green;
  const signals = Object.entries(h.signals);

  return (
    <Card className={`border-slate-200 bg-gradient-to-br ${overallStyle.tint} to-white`} data-testid="firm-health-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Firm Health Score</div>
            <div className={`mt-1 font-serif text-4xl font-bold ${overallStyle.text}`} data-testid="firm-health-score">{h.overall.score}</div>
            <div className="mt-1 text-sm text-slate-600">{h.overall.headline}</div>
          </div>

          <div className="flex items-center gap-4">
            {history && history.points && history.points.length > 1 && (
              <TrendSparkline points={history.points} trend={history.trend} />
            )}
            <div className={`flex h-16 w-16 items-center justify-center rounded-full ring-4 ${overallStyle.ring}`}>
              <div className={`h-10 w-10 rounded-full ${overallStyle.bar}`} data-testid="firm-health-band" data-band={h.overall.band} />
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="firm-health-signals">
          {signals.map(([key, s]) => {
            const st = BAND_STYLES[s.band] || BAND_STYLES.green;
            return (
              <div
                key={key}
                data-testid={`firm-health-signal-${key}`}
                className="rounded-md border border-slate-200 bg-white p-3"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                  <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                  {s.label}
                </div>
                <div className={`mt-1 text-2xl font-bold ${st.text}`}>{s.score}</div>
                <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full ${st.bar}`} style={{ width: `${Math.max(4, Math.min(100, s.score))}%` }} />
                </div>
                <div className="mt-2 text-[11px] text-slate-600 leading-snug">{s.note}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TrendSparkline({ points, trend }) {
  const scored = points.filter((p) => p.overall !== null).map((p) => p.overall);
  if (scored.length < 2) return null;
  const min = Math.min(...scored, 0);
  const max = Math.max(...scored, 100);
  const range = Math.max(1, max - min);
  const W = 140, H = 44;
  const step = W / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * step;
    if (p.overall === null) return null;
    const y = H - ((p.overall - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  }).filter(Boolean);
  const trendColor = { up: 'text-emerald-600', down: 'text-red-600', flat: 'text-slate-500' }[trend] || 'text-slate-500';
  const trendArrow = { up: '▲', down: '▼', flat: '—' }[trend] || '—';
  const strokeColor = { up: '#059669', down: '#DC2626', flat: '#94A3B8' }[trend] || '#94A3B8';
  return (
    <div className="hidden md:flex flex-col items-end" data-testid="firm-health-sparkline">
      <div className="text-[9px] uppercase tracking-wider text-slate-400">30-day trend</div>
      <svg width={W} height={H} className="mt-1">
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={coords.join(' ')}
        />
      </svg>
      <div className={`mt-0.5 text-[10px] ${trendColor}`} data-testid="firm-health-trend" data-trend={trend}>
        {trendArrow} {trend === 'flat' ? 'holding steady' : trend === 'up' ? 'trending up' : 'trending down'}
      </div>
    </div>
  );
}

